/**
 * wd-predictive-search.js — header predictive search (Classic Wonder pattern, D-2).
 *
 * Wraps one search form + its results container. Two instances live on the page
 * (desktop portal + mobile overlay); every DOM query is scoped to `this`, and
 * option ids are stamped per instance, so the two never collide.
 *
 * Transport: Section Rendering on the REGULAR search route.
 *   /search?q=<term>*&type=product&section_id=wd-predictive-results
 * The native /search/suggest endpoint returns HTTP 417 "Unsupported buyer locale"
 * on this store (docs/KNOWN_BUGS.md M-search). /search is unaffected.
 *
 * The trailing `*` is required: measured against the live catalogue, a plain
 * partial term returns ZERO results on this route (`iro`, `ברז`, `מגנ` -> 0),
 * while the wildcard resolves them (-> 3 each). `buildUrl()` is deliberately the
 * single place the request is composed, so a future plain+wildcard merge can be
 * added there without touching anything else.
 *
 * Panel open/close remains owned by snippets/wd-scripts-header.liquid. This
 * component asks to be closed by dispatching `wd-psr:requestclose`, and is told
 * about visibility through `wd-psr:opened` / `wd-psr:closed`.
 */
(function () {
  'use strict';

  /* Guarded so a double include cannot throw and kill the rest of the file (M15). */
  if (window.customElements && customElements.get('wd-predictive-search')) return;

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 220;
  var LOADING_AFTER_MS = 150; /* don't flash a spinner for fast responses */
  var SCROLL_LOCK_CLASS = 'wd-psr-locked';

  /* Characters that carry meaning in Shopify search syntax. Verified live:
     `"iro"*` -> 0 results, a bare `*` -> the entire catalogue. */
  var SYNTAX_CHARS = /[*"()~\\\[\]{}^]/g;
  var OPERATORS = /\b(?:AND|OR|NOT)\b/g;

  var uidCounter = 0;

  /* ====================================================================
     "Opened from search" capture — page level, NOT component level.

     Runs on DOMContentLoaded regardless of whether <wd-predictive-search>
     connects, so a header failure can never take the feature down with it.
     Presence of the server-rendered payload is the enable signal: Liquid only
     emits it when the merchant has recently-viewed switched on.
     ==================================================================== */

  /**
   * Did this product visit originate from search? Three independent signals,
   * because any one of them can be missing in the wild:
   *
   *   1. `_ss` / `_sid` — Shopify appends these to product links rendered in a
   *      search context. Present today, but stripped by some redirects and
   *      absent if a link is rebuilt by a theme.
   *   2. `document.referrer` ending at a /search path — survives parameter
   *      stripping, but is empty under a strict Referrer-Policy.
   *   3. A short-lived pending marker written on the results page before
   *      navigation — survives both of the above failing.
   */
  function searchReferralSignal(productUrl) {
    var qs = window.location.search || '';
    if (qs.indexOf('_ss=') !== -1 || qs.indexOf('_sid=') !== -1) return 'query-params';

    try {
      if (document.referrer) {
        var ref = new URL(document.referrer, window.location.href);
        /* Same-origin only, and the path segment must actually be /search —
           matches /search, /he-il/search, /en/search. */
        if (ref.origin === window.location.origin && /(^|\/)search(\/|$)/.test(ref.pathname)) {
          return 'referrer';
        }
      }
    } catch (e) {
      /* malformed referrer — fall through to the marker */
    }

    if (window.WdRecentProducts && window.WdRecentProducts.matchPending(productUrl)) {
      return 'pending-marker';
    }
    return null;
  }

  function captureCurrentProduct() {
    if (!window.WdRecentProducts) return null;

    var node = document.querySelector('[data-wd-search-current-product]');
    if (!node) return null; /* feature off, or not a product page */

    var rec;
    try {
      rec = JSON.parse(node.textContent);
    } catch (e) {
      return null; /* malformed payload — never break the page over this */
    }
    if (!rec || !rec.url || !rec.title) return null;

    var signal = searchReferralSignal(rec.url);
    /* Always clear the marker on a product page, so a stale one cannot attach
       itself to an unrelated product later. */
    window.WdRecentProducts.clearPending();
    if (!signal) return null;

    var max = parseInt(node.getAttribute('data-max'), 10) || undefined;
    window.WdRecentProducts.add(rec, max);
    return signal;
  }

  /**
   * On the full search-results page, remember the product about to be opened.
   * Tightly scoped to the results grid so nothing else on the page is affected.
   * Never calls preventDefault, so normal navigation, middle-click and
   * cmd/ctrl-click all keep working.
   */
  function initSearchResultsCapture() {
    var grid = document.querySelector('#product-grid, [data-wd-search-results-grid]');
    if (!grid || !window.WdRecentProducts) return;

    grid.addEventListener('click', function (e) {
      var link = e.target && e.target.closest ? e.target.closest('a[href*="/products/"]') : null;
      if (link) window.WdRecentProducts.setPending(link.getAttribute('href'));
    });
    /* Keyboard activation of a focused link fires a click event too, so the
       listener above already covers Enter. */
  }

  function initPageCapture() {
    initSearchResultsCapture();
    captureCurrentProduct();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPageCapture);
  } else {
    initPageCapture();
  }

  function sanitize(raw) {
    return String(raw == null ? '' : raw)
      .replace(SYNTAX_CHARS, ' ')
      .replace(OPERATORS, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* Prefix-match the LAST token only, so multi-word queries keep AND semantics:
     "lipo iro" -> "lipo iro*" (verified: 3 results). */
  function toPrefixQuery(clean) {
    if (!clean) return '';
    var tokens = clean.split(' ');
    tokens[tokens.length - 1] = tokens[tokens.length - 1] + '*';
    return tokens.join(' ');
  }

  function t(el, key, fallback) {
    var v = el.getAttribute(key);
    return v == null || v === '' ? fallback : v;
  }

  var WdPredictiveSearch = class extends HTMLElement {
    constructor() {
      super();
      this._uid = 'wd-psr-' + ++uidCounter;
      this._timer = null;
      this._loadingTimer = null;
      this._controller = null;
      this._reqId = 0;
      this._activeIndex = -1;
      this._options = [];
      this._lastQuery = '';
      this._locked = false;
      this._bound = false;

      this._onInput = this._onInput.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onClear = this._onClear.bind(this);
      this._onComponentClick = this._onComponentClick.bind(this);
      this._onOpened = this._onOpened.bind(this);
      this._onClosed = this._onClosed.bind(this);
      this._onRetry = this._onRetry.bind(this);
      this._onRecentClear = this._onRecentClear.bind(this);
    }

    connectedCallback() {
      /* connectedCallback runs again after a Theme Editor section reload, so the
         guard keeps listeners from stacking up (H6). */
      if (this._bound) return;

      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-wd-search-results]');
      this.clearBtn = this.querySelector('[data-wd-search-clear]');
      if (!this.form || !this.input || !this.results) return;

      /* Pre-typing discovery panel. Optional: entirely absent when the merchant
         has switched off all three blocks. */
      this.emptyState = this.querySelector('[data-wd-search-empty]');
      this.recentBlock = this.emptyState ? this.emptyState.querySelector('[data-wd-recent]') : null;
      this.recentList = this.emptyState ? this.emptyState.querySelector('[data-wd-recent-list]') : null;
      this.recentClearBtn = this.emptyState ? this.emptyState.querySelector('[data-wd-recent-clear]') : null;
      this.recentMax = this.emptyState
        ? parseInt(this.emptyState.getAttribute('data-wd-recent-max'), 10) || 4
        : 4;
      /* Recently-viewed rows are built client-side, so the sold-out label has to
         be handed in from Liquid rather than translated at render time. */
      this.soldOutLabel = this.emptyState
        ? this.emptyState.getAttribute('data-label-soldout') || ''
        : '';

      this.variant = this.getAttribute('data-variant') === 'mobile' ? 'mobile' : 'desktop';
      this.endpoint = this.getAttribute('data-search-url') || '/search';
      this.sectionId = this.getAttribute('data-section-id') || 'wd-predictive-results';

      this.resultsId = this._uid + '-results';
      this.results.id = this.resultsId;

      /* Combobox semantics live on the input; the listbox arrives with the
         rendered section. */
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', this.resultsId);
      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('autocorrect', 'off');
      this.input.setAttribute('spellcheck', 'false');

      this.status = document.createElement('span');
      this.status.className = 'wd-psr__status';
      this.status.setAttribute('aria-live', 'polite');
      this.status.setAttribute('role', 'status');
      this.appendChild(this.status);

      this.input.addEventListener('input', this._onInput);
      this.input.addEventListener('keydown', this._onKeydown);
      this.form.addEventListener('submit', this._onSubmit);
      this.addEventListener('click', this._onComponentClick);
      if (this.clearBtn) this.clearBtn.addEventListener('click', this._onClear);
      if (this.recentClearBtn) this.recentClearBtn.addEventListener('click', this._onRecentClear);
      this.addEventListener('wd-psr:opened', this._onOpened);
      this.addEventListener('wd-psr:closed', this._onClosed);

      this._syncClear();
      this._renderRecent();
      this._setState('idle');
      this._bound = true;
    }

    disconnectedCallback() {
      this._abort();
      if (this._timer) clearTimeout(this._timer);
      if (this._loadingTimer) clearTimeout(this._loadingTimer);
      this._timer = this._loadingTimer = null;

      if (this.input) {
        this.input.removeEventListener('input', this._onInput);
        this.input.removeEventListener('keydown', this._onKeydown);
      }
      if (this.form) this.form.removeEventListener('submit', this._onSubmit);
      this.removeEventListener('click', this._onComponentClick);
      if (this.clearBtn) this.clearBtn.removeEventListener('click', this._onClear);
      if (this.recentClearBtn) this.recentClearBtn.removeEventListener('click', this._onRecentClear);
      this.removeEventListener('wd-psr:opened', this._onOpened);
      this.removeEventListener('wd-psr:closed', this._onClosed);

      this._releaseScroll();
      this._bound = false;
    }

    /* ---------------------------------------------------------------- request */

    /* Single composition point for the predictive request. A future
       plain + wildcard merge belongs here and nowhere else. */
    buildUrl(clean) {
      return (
        this.endpoint +
        '?q=' + encodeURIComponent(toPrefixQuery(clean)) +
        '&type=product' +
        '&section_id=' + encodeURIComponent(this.sectionId)
      );
    }

    _abort() {
      if (this._controller) {
        try { this._controller.abort(); } catch (e) { /* older browsers */ }
        this._controller = null;
      }
    }

    _onInput() {
      this._syncClear();
      if (this._timer) clearTimeout(this._timer);
      var self = this;
      this._timer = setTimeout(function () { self._request(); }, DEBOUNCE_MS);
    }

    _request() {
      var clean = sanitize(this.input.value);

      /* Empty and one-character queries must never hit the network. */
      if (clean.length < MIN_CHARS) {
        this._abort();
        this._reqId++; /* invalidate anything still in flight */
        this._setState('idle');
        return;
      }

      this._lastQuery = clean;
      this._abort();
      this._controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

      var id = ++this._reqId;
      var self = this;

      if (this._loadingTimer) clearTimeout(this._loadingTimer);
      this._loadingTimer = setTimeout(function () {
        if (id === self._reqId) self._setState('loading');
      }, LOADING_AFTER_MS);

      var opts = { headers: { Accept: 'text/html' } };
      if (this._controller) opts.signal = this._controller.signal;

      fetch(this.buildUrl(clean), opts)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          /* Stale-response guard: a slower earlier request must never overwrite
             a newer one, even if abort() did not land. */
          if (id !== self._reqId) return;
          self._render(html);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return; /* superseded — not an error */
          if (id !== self._reqId) return;
          /* Must cancel the pending loading timer here too, or it fires after the
             failure and paints "loading" over the error state. */
          if (self._loadingTimer) clearTimeout(self._loadingTimer);
          self._loadingTimer = null;
          self._setState('error');
        });
    }

    _onRetry() {
      this._request();
    }

    /* ---------------------------------------------------------------- render */

    _render(html) {
      if (this._loadingTimer) clearTimeout(this._loadingTimer);
      this._loadingTimer = null;

      var root = null;
      if (html && html.trim()) {
        try {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          root = doc.querySelector('[data-wd-psr-root]');
        } catch (e) {
          root = null;
        }
      }

      /* A 200 that carries no parsable results is still a failure — surfacing it
         is the whole point (the previous implementation swallowed it silently). */
      if (!root) {
        this._setState('error');
        return;
      }

      this.results.innerHTML = '';
      this.results.appendChild(root);

      var count = parseInt(root.getAttribute('data-wd-psr-count'), 10);
      if (isNaN(count)) count = 0;

      if (count > 0) {
        this._setState('results', count);
      } else {
        this._setState('empty');
      }
    }

    _collectOptions() {
      this._options = Array.prototype.slice.call(
        this.results.querySelectorAll('[data-wd-psr-option]')
      );
      var self = this;
      this._options.forEach(function (opt, i) {
        opt.id = self._uid + '-opt-' + i;
        opt.setAttribute('aria-selected', 'false');
      });
      var list = this.results.querySelector('[data-wd-psr-list]');
      if (list) list.id = this._uid + '-list';
      this._activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
    }

    _setState(state, count) {
      this.setAttribute('data-state', state);

      var expanded = state === 'results' || state === 'empty' || state === 'error' || state === 'loading';
      this.input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.results.hidden = !expanded;

      /* The discovery panel and the results listbox are mutually exclusive —
         never both on screen at once. */
      this._syncEmptyState(state === 'idle');

      if (state === 'idle') {
        this.results.innerHTML = '';
        this._options = [];
        this._activeIndex = -1;
        this.input.removeAttribute('aria-activedescendant');
        this._announce('');
        return;
      }

      if (state === 'loading') {
        this.results.innerHTML =
          '<p class="wd-psr__loading" role="presentation">' +
          this._escape(t(this, 'data-label-loading', 'Loading…')) +
          '</p>';
        this._options = [];
        this._announce(t(this, 'data-label-loading', 'Loading…'));
        return;
      }

      if (state === 'error') {
        var label = t(this, 'data-label-error', 'Something went wrong.');
        var retry = t(this, 'data-label-retry', 'Try again');
        this.results.innerHTML =
          '<div class="wd-psr__error"><p class="wd-psr__error-text">' +
          this._escape(label) +
          '</p><button type="button" class="wd-psr__retry" data-wd-psr-retry>' +
          this._escape(retry) +
          '</button></div>';
        this._options = [];
        this._activeIndex = -1;
        this.input.removeAttribute('aria-activedescendant');
        this._announce(label);
        return;
      }

      if (state === 'empty') {
        this._options = [];
        this._activeIndex = -1;
        this.input.removeAttribute('aria-activedescendant');
        var emptyEl = this.results.querySelector('[data-wd-psr-empty]');
        this._announce(emptyEl ? emptyEl.textContent.trim() : '');
        return;
      }

      /* results — the count sentence is rendered server-side so Liquid keeps
         ownership of pluralisation and translation. */
      this._collectOptions();
      var ann = this.results.querySelector('[data-wd-psr-announce]');
      this._announce(ann ? ann.textContent.trim() : '');
    }

    _announce(msg) {
      if (this.status) this.status.textContent = msg || '';
    }

    /* ------------------------------------------- empty state / recent searches */

    /**
     * Show the discovery panel only when idle AND it actually has something to
     * show. With recent searches enabled but no history yet, and popular and
     * featured both switched off, there is nothing to display — an empty
     * bordered box would look broken, so the panel stays hidden.
     */
    _syncEmptyState(shouldShow) {
      if (!this.emptyState) return;
      if (!shouldShow) {
        this.emptyState.hidden = true;
        return;
      }
      var blocks = this.emptyState.querySelectorAll('.wd-ses__block');
      var visible = 0;
      for (var i = 0; i < blocks.length; i++) {
        if (!blocks[i].hidden) visible++;
      }
      this.emptyState.hidden = visible === 0;
    }

    /* Mirrors the markup of snippets/wd-psr-product.liquid so the stored rows
       reuse exactly the same CSS as predictive results and featured products.
       Built node by node with textContent — a stored title must never be able
       to become markup. */
    _buildProductRow(rec) {
      var doc = this.ownerDocument;
      var li = doc.createElement('li');
      li.className = 'wd-psr__item';

      var a = doc.createElement('a');
      a.className = 'wd-psr__link';
      a.href = rec.url;

      var thumb = doc.createElement('span');
      thumb.className = 'wd-psr__thumb';
      if (rec.image) {
        var img = doc.createElement('img');
        img.className = 'wd-psr__img';
        img.src = rec.image;
        img.width = 56;
        img.height = 56;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        thumb.appendChild(img);
      } else {
        /* Same placeholder glyph the Liquid snippet renders. */
        var ph = doc.createElement('span');
        ph.className = 'wd-psr__thumb-fallback';
        ph.setAttribute('aria-hidden', 'true');
        var NS = 'http://www.w3.org/2000/svg';
        var svg = doc.createElementNS(NS, 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('width', '20');
        svg.setAttribute('height', '20');
        svg.setAttribute('fill', 'none');
        var p1 = doc.createElementNS(NS, 'path');
        p1.setAttribute('d', 'M3 5.5A1.5 1.5 0 0 1 4.5 4h15A1.5 1.5 0 0 1 21 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5v-13Z');
        p1.setAttribute('stroke', 'currentColor');
        p1.setAttribute('stroke-width', '1.4');
        var p2 = doc.createElementNS(NS, 'path');
        p2.setAttribute('d', 'm3 16 5-4 4 3 3.5-3L21 16');
        p2.setAttribute('stroke', 'currentColor');
        p2.setAttribute('stroke-width', '1.4');
        p2.setAttribute('stroke-linejoin', 'round');
        svg.appendChild(p1);
        svg.appendChild(p2);
        ph.appendChild(svg);
        thumb.appendChild(ph);
      }
      a.appendChild(thumb);

      var info = doc.createElement('span');
      info.className = 'wd-psr__info';

      var title = doc.createElement('span');
      title.className = 'wd-psr__title';
      var titleBdi = doc.createElement('bdi');
      titleBdi.textContent = rec.title;
      title.appendChild(titleBdi);
      info.appendChild(title);

      var meta = doc.createElement('span');
      meta.className = 'wd-psr__meta';

      /* A missing price must not break the row — the rest still renders. */
      if (rec.price) {
        var price = doc.createElement('span');
        price.className = 'wd-psr__price';
        var priceBdi = doc.createElement('bdi');
        priceBdi.textContent = rec.price;
        price.appendChild(priceBdi);
        meta.appendChild(price);
      }
      if (rec.compareAtPrice) {
        var s = doc.createElement('s');
        s.className = 'wd-psr__compare';
        var cmpBdi = doc.createElement('bdi');
        cmpBdi.textContent = rec.compareAtPrice;
        s.appendChild(cmpBdi);
        meta.appendChild(s);
      }
      if (rec.available === false) {
        var badge = doc.createElement('span');
        badge.className = 'wd-psr__badge';
        badge.textContent = this.soldOutLabel;
        meta.appendChild(badge);
      }

      info.appendChild(meta);
      a.appendChild(info);
      li.appendChild(a);
      return li;
    }

    _renderRecent() {
      if (!this.recentBlock || !this.recentList) return;

      var store = window.WdRecentProducts;
      var items = store ? store.get(this.recentMax) : [];

      this.recentList.innerHTML = '';
      if (!items.length) {
        this.recentBlock.hidden = true;
        return;
      }

      var frag = this.ownerDocument.createDocumentFragment();
      for (var i = 0; i < items.length; i++) {
        frag.appendChild(this._buildProductRow(items[i]));
      }
      this.recentList.appendChild(frag);
      this.recentBlock.hidden = false;
    }

    _onRecentClear() {
      if (window.WdRecentProducts) window.WdRecentProducts.clear();
      this._renderRecent();
      this._syncEmptyState(this.getAttribute('data-state') === 'idle');
      /* The button that was just clicked is now hidden with its block, so move
         focus somewhere sensible rather than letting it fall to <body>. */
      this.input.focus();
    }

    /**
     * Read the product straight off the row the customer is opening, so what we
     * store is exactly what was on screen. Only ever called for a real product
     * navigation — never for typing, view-all, a popular chip or a bare submit.
     */
    _extractProduct(link) {
      var titleEl = link.querySelector('.wd-psr__title');
      var priceEl = link.querySelector('.wd-psr__price');
      var compareEl = link.querySelector('.wd-psr__compare');
      var imgEl = link.querySelector('.wd-psr__img');
      var badgeEl = link.querySelector('.wd-psr__badge');
      var href = link.getAttribute('href');
      if (!href || !titleEl) return null;

      return {
        url: href,
        title: titleEl.textContent.replace(/\s+/g, ' ').trim(),
        image: imgEl ? imgEl.currentSrc || imgEl.getAttribute('src') || '' : '',
        price: priceEl ? priceEl.textContent.replace(/\s+/g, ' ').trim() : '',
        compareAtPrice: compareEl ? compareEl.textContent.replace(/\s+/g, ' ').trim() : '',
        available: !badgeEl,
      };
    }

    _captureProduct(link) {
      if (!this.emptyState || !this.emptyState.hasAttribute('data-wd-recent-enabled')) return;
      if (!window.WdRecentProducts || !link) return;
      var rec = this._extractProduct(link);
      if (!rec) return;
      window.WdRecentProducts.add(rec, this.recentMax);
    }

    _escape(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    /* ------------------------------------------------------------- keyboard */

    _setActive(index) {
      if (!this._options.length) return;
      if (this._activeIndex > -1 && this._options[this._activeIndex]) {
        this._options[this._activeIndex].setAttribute('aria-selected', 'false');
        this._options[this._activeIndex].classList.remove('is-active');
      }
      this._activeIndex = index;
      var opt = this._options[index];
      if (!opt) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }
      opt.setAttribute('aria-selected', 'true');
      opt.classList.add('is-active');
      this.input.setAttribute('aria-activedescendant', opt.id);
      if (opt.scrollIntoView) opt.scrollIntoView({ block: 'nearest' });
    }

    _onKeydown(e) {
      var n = this._options.length;

      if (e.key === 'Escape') {
        e.preventDefault();
        this._requestClose();
        return;
      }

      if (e.key === 'ArrowDown') {
        if (!n) return;
        e.preventDefault();
        this._setActive(this._activeIndex + 1 >= n ? 0 : this._activeIndex + 1);
        return;
      }

      if (e.key === 'ArrowUp') {
        if (!n) return;
        e.preventDefault();
        this._setActive(this._activeIndex - 1 < 0 ? n - 1 : this._activeIndex - 1);
        return;
      }

      if (e.key === 'Enter') {
        if (this._activeIndex > -1 && this._options[this._activeIndex]) {
          e.preventDefault();
          /* Keyboard equivalent of clicking a predictive result — capture the
             selected product, not the partial query that surfaced it. */
          var chosen = this._options[this._activeIndex];
          this._captureProduct(chosen);
          window.location.href = chosen.href;
        }
        /* otherwise fall through: the form submits to the normal results page */
        return;
      }

      if (e.key === 'Tab' && this.variant === 'mobile') {
        this._trapFocus(e);
      }
    }

    _onSubmit() {
      /* Deliberately records nothing: submitting goes to the general results
         page, which identifies no specific product. */
      /* Never block normal navigation — the full-page search must always work. */
      this._abort();
      if (this._timer) clearTimeout(this._timer);
      if (this._loadingTimer) clearTimeout(this._loadingTimer);
    }

    /**
     * One delegated click listener for the whole component.
     * `.wd-psr__link` is the product row used by predictive results, featured
     * products and recently viewed alike — so a click on any of them is a real
     * product navigation and gets captured. Popular chips (.wd-ses__chip) and
     * view-all (.wd-psr__all) are not product rows and are correctly ignored.
     */
    _onComponentClick(e) {
      if (!e.target || !e.target.closest) return;

      if (e.target.closest('[data-wd-psr-retry]')) {
        e.preventDefault();
        this._onRetry();
        return;
      }

      var link = e.target.closest('a.wd-psr__link');
      if (link) this._captureProduct(link);
    }

    _onClear() {
      this.input.value = '';
      this._syncClear();
      this._abort();
      this._reqId++;
      if (this._timer) clearTimeout(this._timer);
      this._setState('idle');
      this.input.focus();
    }

    _syncClear() {
      if (this.clearBtn) this.clearBtn.hidden = this.input.value.length === 0;
    }

    _requestClose() {
      this.dispatchEvent(
        new CustomEvent('wd-psr:requestclose', { bubbles: true, detail: { variant: this.variant } })
      );
    }

    /* --------------------------------------------------- open / close hooks */

    _onOpened() {
      /* Re-read history on every open: the customer may have searched from the
         other surface, or in another tab, since this instance last rendered. */
      this._renderRecent();
      this._syncEmptyState(this.getAttribute('data-state') === 'idle');

      /* The page is deliberately NOT scroll-locked while search is open.
         It used to be, on the mobile variant. But the panel only covers the top
         of the screen and there is no scrim behind it, so the rest of the page
         stayed visible and looked interactive while silently refusing to
         scroll — customers read that as the site having frozen, which is
         exactly what was reported.

         Letting the page scroll is safe here because the panel lives inside the
         header, which is `position: sticky; top: 0`. The panel therefore stays
         pinned to the top while the page moves behind it, rather than scrolling
         away out of reach. The panel's own list still scrolls internally via
         `.wd-ses` (`overflow-y: auto`, capped at `calc(100dvh - 190px)`). */
    }

    _onClosed() {
      this._abort();
      this._reqId++;
      if (this._timer) clearTimeout(this._timer);
      if (this._loadingTimer) clearTimeout(this._loadingTimer);
      this._setState('idle');
      this._releaseScroll();
    }

    /* Nothing adds the lock class any more (see _onOpened). This stays as a
       cleanup for one real case: a visitor whose browser still has the previous
       version of this file cached can have the class left on <html> from before
       the update. Clearing it on close and on disconnect means such a page
       repairs itself instead of staying unscrollable until a hard reload.
       Unconditional on purpose — the old `if (!this._locked) return` guard would
       skip exactly the stale-class case this now exists to handle. */
    _releaseScroll() {
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
      this._locked = false;
    }

    _focusables() {
      var nodes = Array.prototype.slice.call(
        this.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')
      );
      var closeSel = this.getAttribute('data-close-selector');
      if (closeSel) {
        var closeEl = document.querySelector(closeSel);
        if (closeEl) nodes.push(closeEl);
      }
      return nodes.filter(function (el) {
        return !el.hasAttribute('hidden') && el.offsetParent !== null;
      });
    }

    _trapFocus(e) {
      var nodes = this._focusables();
      if (nodes.length < 2) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  customElements.define('wd-predictive-search', WdPredictiveSearch);
})();
